import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { withPreference } from '@bingads-webui-react/preference-context';
import PropTypes from 'prop-types';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withCampaignType } from '@bingads-webui-campaign-react/with-campaign-type';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withAppPerfMarker } from '../contexts/app-perfmarker-context';

const dateRangePreferenceName = 'WebApp.CampaignUI.dateFilter';

export const withPagePerfMarker = (WrappedComponent, perfMarkerName, autoEnd = true) => {
  const getNewState = (appPerfMarker, { pathname }, preferenceService) => {
    const dateRangePreference = preferenceService.findByNameAtUserLevel(dateRangePreferenceName);
    const childPerfMarker = appPerfMarker.createChild(perfMarkerName, { autoEnd, data: { DateRange: dateRangePreference.Data } });
    childPerfMarker.willFetchFile();

    return {
      childPerfMarker,
      pathname,
    };
  };

  const PerfMarkerWrapper = ({
    appPerfMarker,
    preferenceService,
    ...others
  }) => {
    const location = useLocation();
    const [state, setState] = useState(getNewState(appPerfMarker, location, preferenceService));

    if (state.pathname !== location.pathname) {
      setState(getNewState(appPerfMarker, location, preferenceService));
    }

    return (<WrappedComponent perfMarker={state.childPerfMarker} {...others} />);
  };

  PerfMarkerWrapper.propTypes = {
    appPerfMarker: perfMarkerPropTypes.isRequired,
    preferenceService: PropTypes.shape({}).isRequired,
  };

  return withAppPerfMarker(withPreference(React.memo(PerfMarkerWrapper)));
};

export const withOverviewPerfMarker = (WrappedComponent, markerName) => {
  const Wrapper = (props) => {
    const {
      campaignType,
      serverContext,
    } = props;
    let perfMarkerName = markerName || 'Route_Overview_VNext';
    const isLinkedInCampaign = campaignType === 'LinkedIn' && serverContext.DynamicPermissions.LinkedInCampaigns;
    if (perfMarkerName === 'Route_AccountsOverview_VNext') {
      if (serverContext.DynamicPermissions.CustomerLevelOverviewFluent) {
        perfMarkerName = 'Route_AccountsOverview_Fluent2_VNext';
      }
    } else if (campaignType === 'PerformanceMax' && (serverContext.DynamicPermissions.PMaxLite)) {
      perfMarkerName = 'Route_Overview_PMaxLite_VNext';
    } else if (serverContext.DynamicPermissions.OverviewPageFluentV2 || serverContext.DynamicPermissions.OverviewPageFluent2 || serverContext.DynamicPermissions.AppAdsCampaignPhaseOne || serverContext.DynamicPermissions.UnifiedAppAds || isLinkedInCampaign) {
      perfMarkerName = 'Route_Overview_Fluent2_VNext';
    }

    // Auto end is marked as false for the Old Overview because once first two cards are rendered, parent marker autoends while other cards are still being imported.
    const autoEndForParentMarker = perfMarkerName !== 'Route_Overview_VNext';

    const Component = withPagePerfMarker(WrappedComponent, perfMarkerName, autoEndForParentMarker);
    return (<Component {...props} />);
  };
  Wrapper.propTypes = {
    campaignType: PropTypes.string,
    serverContext: PropTypes.shape({
      DynamicPermissions: PropTypes.shape({
        PMaxLite: PropTypes.bool,
        OverviewPageFluentV2: PropTypes.bool,
        OverviewPageFluent2: PropTypes.bool,
      }).isRequired,
    }).isRequired,
  };
  Wrapper.defaultProps = {
    campaignType: undefined,
  };
  return withCampaignType(withServerContext(Wrapper));
};
